/**
 * Mise en page et rendu du graphe d'accessibilite, en fonctions PURES.
 *
 * Le SVG est construit ici, pas dans le composant : la modale affiche exactement la chaine que
 * le bouton « Exporter » telecharge, donc l'export ne peut pas diverger de ce que l'editeur a
 * sous les yeux. Et une chaine se teste, contrairement a un canevas dessine par le navigateur.
 */

export type ReachabilityNodeKind = "home" | "page" | "liste" | "gabarit" | "cible" | string;

export type ReachabilityNode = {
	id: string;
	label: string;
	path: string;
	kind: ReachabilityNodeKind;
};

export type ReachabilityEdge = {
	from: string;
	to: string;
	kind: string;
	label?: string | null;
	/** Le bloc qui porte le lien, tel qu'il apparait dans l'editeur de page. */
	widget?: string | null;
	widgetTitle?: string | null;
	/** Rang de l'entree dans ce bloc, a partir de 1 (listes fixes seulement). */
	itemIndex?: number | null;
	itemCount?: number | null;
	itemTitle?: string | null;
	/** Description deja redigee par le serveur, source unique de la formulation. */
	via?: string | null;
	/** Le lien vient du gabarit du site : il est present sur TOUTES les pages, donc la cible est
	 *  a un clic depuis n'importe ou, pas seulement depuis l'accueil. */
	isGlobal?: boolean | null;
	/** Zone permanente qui porte le lien : menu principal, liens rapides, pied de page. */
	globalArea?: string | null;
};

export type ReachabilityPath = { nodeIds: string[] };

export type ReachabilityReport = {
	contentId: string;
	language: string;
	title: string;
	publicPath: string;
	verdict: string;
	verdictLabel: string;
	nodes: ReachabilityNode[];
	edges: ReachabilityEdge[];
	paths: ReachabilityPath[];
	truncated: boolean;
	loopDetected: boolean;
	maxDepth: number;
};

export type LaidOutNode = ReachabilityNode & {
	column: number;
	row: number;
	x: number;
	y: number;
};

export type LaidOutEdge = ReachabilityEdge & {
	/** Attribut « d » du chemin SVG, deja calcule. */
	d: string;
	/** Raccourci lateral ou retour en arriere : pas un pas de plus vers la cible. */
	sideways: boolean;
};

export type ReachabilityLayout = {
	nodes: LaidOutNode[];
	edges: LaidOutEdge[];
	width: number;
	height: number;
	columns: number;
};

export const NODE_WIDTH = 214;
export const NODE_HEIGHT = 68;
const COLUMN_GAP = 66;
const ROW_GAP = 18;
const MARGIN = 26;

const NODE_STYLE: Record<string, { fill: string; stroke: string; text: string }> = {
	home: { fill: "#0F172A", stroke: "#0F172A", text: "#FFFFFF" },
	page: { fill: "#FFFFFF", stroke: "#CBD5E1", text: "#0F172A" },
	liste: { fill: "#EFF6FF", stroke: "#93C5FD", text: "#1E3A8A" },
	gabarit: { fill: "#F5F3FF", stroke: "#C4B5FD", text: "#4C1D95" },
	cible: { fill: "#ECFDF5", stroke: "#34D399", text: "#065F46" }
};

const EDGE_COLOR: Record<string, string> = {
	menu: "#2563EB",
	lien: "#64748B",
	liste: "#7C3AED"
};

export const nodeStyleFor = (kind: string) => NODE_STYLE[kind] ?? NODE_STYLE.page;
export const edgeColorFor = (kind: string) => EDGE_COLOR[kind] ?? EDGE_COLOR.lien;

export const KIND_LABEL: Record<string, string> = {
	home: "Accueil",
	page: "Page",
	liste: "Page de liste",
	gabarit: "Gabarit de détail",
	cible: "Contenu analysé"
};

export const EDGE_LABEL: Record<string, string> = {
	menu: "Navigation permanente",
	lien: "Lien dans la page",
	liste: "Listé par la page"
};

/**
 * Colonne = nombre de clics MINIMUM depuis l'accueil (parcours en largeur).
 *
 * Le plus LONG chemin donnerait a chaque ecran sa propre colonne et aplatirait l'arbre en une
 * seule ligne : deux entrees de menu partant de l'accueil se retrouveraient l'une derriere
 * l'autre au lieu d'etre deux branches. Avec la distance minimale, les freres partagent leur
 * colonne et la ramification redevient visible.
 */
export const assignColumns = (
	nodes: ReachabilityNode[],
	edges: ReachabilityEdge[],
	homeId: string,
	targetId?: string | null
): Map<string, number> => {
	const known = new Set(nodes.map((node) => node.id));
	const outgoing = new Map<string, string[]>();
	edges.forEach((edge) => {
		if (!known.has(edge.from) || !known.has(edge.to) || edge.from === edge.to) return;
		const list = outgoing.get(edge.from);
		if (list) list.push(edge.to);
		else outgoing.set(edge.from, [edge.to]);
	});

	const columns = new Map<string, number>();
	if (known.has(homeId)) {
		columns.set(homeId, 0);
		let frontier = [homeId];
		let depth = 0;
		while (frontier.length > 0 && depth < nodes.length) {
			depth += 1;
			const next: string[] = [];
			frontier.forEach((id) => {
				(outgoing.get(id) ?? []).forEach((to) => {
					if (columns.has(to)) return;
					columns.set(to, depth);
					next.push(to);
				});
			});
			frontier = next;
		}
	}

	// Un ecran qu'aucune arete n'atteint reste affiche : on le range apres les autres plutot que
	// de le superposer a l'accueil.
	let deepest = 0;
	columns.forEach((value) => {
		if (value > deepest) deepest = value;
	});
	nodes.forEach((node) => {
		if (!columns.has(node.id)) columns.set(node.id, deepest + 1);
	});

	// La cible se lit toujours a droite : c'est la question posee, elle termine la lecture.
	if (targetId && columns.has(targetId)) {
		let others = 0;
		columns.forEach((value, key) => {
			if (key !== targetId && value > others) others = value;
		});
		columns.set(targetId, Math.max(columns.get(targetId) ?? 0, others + (nodes.length > 1 ? 1 : 0)));
	}

	return columns;
};

const nodeOrderKey = (report: ReachabilityReport): Map<string, number> => {
	// Ordre de lecture : l'ordre d'apparition dans les chemins, du plus court au plus long.
	const order = new Map<string, number>();
	let next = 0;
	report.paths.forEach((path) => {
		path.nodeIds.forEach((id) => {
			if (!order.has(id)) {
				order.set(id, next);
				next += 1;
			}
		});
	});
	report.nodes.forEach((node) => {
		if (!order.has(node.id)) {
			order.set(node.id, next);
			next += 1;
		}
	});
	return order;
};

/** Toutes les liaisons qui MENENT a cet ecran, dans l'ordre ou le serveur les a trouvees. */
export const incomingEdges = (report: ReachabilityReport, nodeId: string): ReachabilityEdge[] =>
	report.edges.filter((edge) => edge.to === nodeId);

/**
 * La liaison resumee en quelques caracteres, pour tenir DANS le noeud : le bloc porteur puis le
 * rang. Le detail redige reste dans l'infobulle et dans la liste des chemins.
 */
export const condenseVia = (edge?: ReachabilityEdge | null): string => {
	if (!edge) return "";
	const block = String(edge.widgetTitle || edge.widget || EDGE_LABEL[edge.kind] || edge.kind || "").trim();
	const rank =
		edge.itemIndex && edge.itemCount
			? `${edge.itemIndex}/${edge.itemCount}`
			: edge.itemCount
				? `${edge.itemCount}/page`
				: "";
	return [block, rank].filter(Boolean).join(" · ");
};

const round = (value: number) => Math.round(value * 10) / 10;

export const layoutReachability = (report: ReachabilityReport): ReachabilityLayout => {
	const homeId = report.nodes.find((node) => node.kind === "home")?.id ?? report.nodes[0]?.id ?? "home";
	const targetId = report.nodes.find((node) => node.kind === "cible")?.id ?? null;
	const columns = assignColumns(report.nodes, report.edges, homeId, targetId);
	const order = nodeOrderKey(report);

	const byColumn = new Map<number, ReachabilityNode[]>();
	report.nodes.forEach((node) => {
		const column = columns.get(node.id) ?? 0;
		const bucket = byColumn.get(column);
		if (bucket) bucket.push(node);
		else byColumn.set(column, [node]);
	});

	const columnKeys = Array.from(byColumn.keys()).sort((a, b) => a - b);
	const tallest = columnKeys.reduce((max, key) => Math.max(max, byColumn.get(key)?.length ?? 0), 0);
	const height = MARGIN * 2 + Math.max(1, tallest) * NODE_HEIGHT + Math.max(0, tallest - 1) * ROW_GAP;

	const laidOut: LaidOutNode[] = [];
	const rowOf = new Map<string, number>();

	columnKeys.forEach((column, columnIndex) => {
		// Chaque enfant se place en face de ses parents (barycentre), sinon les branches se
		// croisent et l'arbre devient illisible des le troisieme chemin.
		const anchorOf = (node: ReachabilityNode) => {
			const parents = report.edges
				.filter((edge) => edge.to === node.id && (columns.get(edge.from) ?? 0) < column)
				.map((edge) => rowOf.get(edge.from))
				.filter((row): row is number => row !== undefined);
			if (parents.length === 0) return Number.MAX_SAFE_INTEGER;
			return parents.reduce((sum, row) => sum + row, 0) / parents.length;
		};

		const bucket = (byColumn.get(column) ?? []).slice().sort((a, b) => {
			const delta = anchorOf(a) - anchorOf(b);
			if (delta !== 0) return delta;
			return (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
		});

		const blockHeight = bucket.length * NODE_HEIGHT + Math.max(0, bucket.length - 1) * ROW_GAP;
		const top = (height - blockHeight) / 2;
		bucket.forEach((node, row) => {
			rowOf.set(node.id, row);
			laidOut.push({
				...node,
				column: columnIndex,
				row,
				x: MARGIN + columnIndex * (NODE_WIDTH + COLUMN_GAP),
				y: top + row * (NODE_HEIGHT + ROW_GAP)
			});
		});
	});

	const width =
		MARGIN * 2 + Math.max(1, columnKeys.length) * NODE_WIDTH + Math.max(0, columnKeys.length - 1) * COLUMN_GAP;
	const positions = new Map(laidOut.map((node) => [node.id, node]));

	let deepestDrop = 0;
	let widestLoop = 0;
	const edges: LaidOutEdge[] = report.edges
		.filter((edge) => positions.has(edge.from) && positions.has(edge.to))
		.map((edge) => {
			const from = positions.get(edge.from) as LaidOutNode;
			const to = positions.get(edge.to) as LaidOutNode;
			if (to.x > from.x) {
				const x1 = from.x + NODE_WIDTH;
				const y1 = from.y + NODE_HEIGHT / 2;
				const x2 = to.x;
				const y2 = to.y + NODE_HEIGHT / 2;
				const bend = Math.max(28, (x2 - x1) / 2);
				return {
					...edge,
					sideways: false,
					d: `M ${round(x1)} ${round(y1)} C ${round(x1 + bend)} ${round(y1)}, ${round(x2 - bend)} ${round(y2)}, ${round(x2)} ${round(y2)}`
				};
			}
			if (to.x === from.x) {
				// Deux ecrans a la meme distance de l'accueil qui se citent : boucle sur le cote,
				// un trait vertical entre deux cases voisines ne se lit pas comme une liaison.
				const x1 = from.x + NODE_WIDTH;
				const y1 = from.y + NODE_HEIGHT / 2;
				const x2 = to.x + NODE_WIDTH;
				const y2 = to.y + NODE_HEIGHT / 2;
				const bulge = 26 + 12 * Math.abs(from.row - to.row);
				widestLoop = Math.max(widestLoop, x1 + bulge);
				return {
					...edge,
					sideways: true,
					d: `M ${round(x1)} ${round(y1)} C ${round(x1 + bulge)} ${round(y1)}, ${round(x2 + bulge)} ${round(y2)}, ${round(x2)} ${round(y2)}`
				};
			}

			// Retour en arriere : on passe par-dessous, plutot que de tracer une fleche qui
			// traverse les autres noeuds a rebours. Le creux se creuse avec l'ecart de lignes,
			// sinon deux retours voisins se superposent en un seul trait illisible.
			const x1 = from.x + NODE_WIDTH / 2;
			const y1 = from.y + NODE_HEIGHT;
			const x2 = to.x + NODE_WIDTH / 2;
			const y2 = to.y + NODE_HEIGHT;
			const drop = 22 + 14 * Math.abs(from.row - to.row);
			deepestDrop = Math.max(deepestDrop, from.y + NODE_HEIGHT + drop, to.y + NODE_HEIGHT + drop);
			return {
				...edge,
				sideways: true,
				d: `M ${round(x1)} ${round(y1)} C ${round(x1)} ${round(y1 + drop)}, ${round(x2)} ${round(y2 + drop)}, ${round(x2)} ${round(y2)}`
			};
		});

	// Boucles et retours sortent du rectangle des noeuds : sans cette reserve, leur courbe est
	// coupee a l'ecran comme a l'export.
	const fullHeight = Math.max(height, deepestDrop > 0 ? deepestDrop + 10 : 0);
	const fullWidth = Math.max(width, widestLoop > 0 ? widestLoop + 10 : 0);

	return { nodes: laidOut, edges, width: fullWidth, height: fullHeight, columns: columnKeys.length };
};

export const escapeXml = (value: string): string =>
	String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");

export const truncateLabel = (value: string, max = 28): string => {
	const text = String(value ?? "").trim();
	if (text.length <= max) return text;
	return `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
};

/**
 * Le graphe complet en un SVG autonome : aucune feuille de style externe, aucune police exotique.
 * Ce qui est affiche est ce qui est exporte, et le fichier s'ouvre partout.
 *
 * Chaque noeud porte lui-meme ce qu'il faut savoir : l'ecran, son adresse, et par quel bloc on y
 * entre. Lire le graphe suffit ; la liste des chemins n'est qu'un complement.
 */
export const buildReachabilitySvg = (report: ReachabilityReport): string => {
	const layout = layoutReachability(report);
	const font = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

	const markers = Object.entries(EDGE_COLOR)
		.map(
			([kind, color]) =>
				`<marker id="arrow-${escapeXml(kind)}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="${color}"/></marker>`
		)
		.join("");

	const edges = layout.edges
		.map((edge) => {
			const color = edgeColorFor(edge.kind);
			const nature = EDGE_LABEL[edge.kind] ?? edge.kind;
			const detail = [edge.label, edge.via].filter((part) => part && String(part).trim()).join(" · ");
			// Pointille pour un raccourci lateral : l'oeil ne le compte pas comme un clic de plus.
			const dash = edge.sideways ? ' stroke-dasharray="5 4"' : "";
			return `<path d="${escapeXml(edge.d)}" fill="none" stroke="${color}" stroke-width="1.6"${dash} marker-end="url(#arrow-${escapeXml(
				edge.kind
			)})" opacity="0.8"><title>${escapeXml(detail ? `${nature} : ${detail}` : nature)}</title></path>`;
		})
		.join("");

	const nodes = layout.nodes
		.map((node) => {
			const style = nodeStyleFor(node.kind);
			const subtitle = node.kind === "home" ? "/" : node.path ? `/${node.path.replace(/^\/+/, "")}` : "";
			const routes = incomingEdges(report, node.id);
			const via = condenseVia(routes[0]);
			// « +2 » dit qu'on peut aussi arriver ici par deux autres blocs : l'infobulle les liste.
			const extra = routes.length > 1 ? `  +${routes.length - 1}` : "";
			const viaColor = routes[0] ? edgeColorFor(routes[0].kind) : "#94A3B8";
			const tooltip = [
				node.label,
				subtitle,
				...routes.map((route) => `${EDGE_LABEL[route.kind] ?? route.kind} : ${route.via || route.label || ""}`)
			]
				.filter((line) => line && String(line).trim())
				.join("\n");

			return [
				"<g>",
				`<title>${escapeXml(tooltip)}</title>`,
				`<rect x="${round(node.x)}" y="${round(node.y)}" width="${NODE_WIDTH}" height="${NODE_HEIGHT}" rx="10" fill="${style.fill}" stroke="${style.stroke}" stroke-width="1.5"/>`,
				`<text x="${round(node.x + 12)}" y="${round(node.y + 22)}" font-family="${font}" font-size="12.5" font-weight="600" fill="${style.text}">${escapeXml(truncateLabel(node.label))}</text>`,
				subtitle
					? `<text x="${round(node.x + 12)}" y="${round(node.y + 38)}" font-family="${font}" font-size="10" fill="${
							node.kind === "home" ? "#94A3B8" : "#64748B"
						}">${escapeXml(truncateLabel(subtitle, 34))}</text>`
					: "",
				via
					? `<text x="${round(node.x + 12)}" y="${round(node.y + 55)}" font-family="${font}" font-size="9.5" font-weight="600" fill="${viaColor}">${escapeXml(
							truncateLabel(via + extra, 34)
						)}</text>`
					: "",
				"</g>"
			].join("");
		})
		.join("");

	return [
		`<svg xmlns="http://www.w3.org/2000/svg" width="${layout.width}" height="${layout.height}" viewBox="0 0 ${layout.width} ${layout.height}" role="img">`,
		`<title>${escapeXml(`Chemin d acces a ${report.title}`)}</title>`,
		`<desc>${escapeXml(report.verdictLabel)}</desc>`,
		`<defs>${markers}</defs>`,
		`<rect width="${layout.width}" height="${layout.height}" fill="#FFFFFF"/>`,
		edges,
		nodes,
		"</svg>"
	].join("");
};

/** Un chemin rendu lisible : « Accueil > La Banque > Missions ». */
export const describePath = (report: ReachabilityReport, path: ReachabilityPath): string[] => {
	const labels = new Map(report.nodes.map((node) => [node.id, node.label]));
	return path.nodeIds.map((id) => labels.get(id) ?? id);
};

export type ReachabilityStep = {
	id: string;
	label: string;
	/** Chemin public de l'ecran, prefixe d'une barre oblique ; vide pour l'accueil. */
	path: string;
	/** Comment on est arrive ICI depuis l'ecran precedent : le bloc et le rang de l'entree. */
	via: string;
	/** Nature de la liaison empruntee, pour la couleur : menu / lien / liste. */
	viaKind: string;
	/** Le lien suivi est celui du gabarit du site : on y accede depuis n'importe quelle page. */
	fromAnywhere: boolean;
};

/**
 * Le chemin detaille, etape par etape. « Le lien est sur cette page » ne suffit pas quand la page
 * porte trente-cinq blocs : chaque etape dit dans quel bloc et a quelle place se trouve le lien
 * suivi, en reprenant telle quelle la phrase calculee par le serveur.
 */
export const describePathSteps = (report: ReachabilityReport, path: ReachabilityPath): ReachabilityStep[] => {
	const nodes = new Map(report.nodes.map((node) => [node.id, node]));
	const edgeAt = (from: string, to: string) => report.edges.find((edge) => edge.from === from && edge.to === to);

	return path.nodeIds.map((id, index) => {
		const node = nodes.get(id);
		const edge = index > 0 ? edgeAt(path.nodeIds[index - 1], id) : undefined;
		const via = edge?.via?.trim() || (edge?.label ? `« ${edge.label} »` : "");
		return {
			id,
			label: node?.label ?? id,
			path: node && node.kind !== "home" && node.path ? `/${node.path.replace(/^\/+/, "")}` : node?.kind === "home" ? "/" : "",
			via,
			viaKind: edge?.kind ?? "",
			fromAnywhere: Boolean(edge?.isGlobal)
		};
	});
};

/** Nom de fichier stable, horodate par l'appelant (aucune horloge dans une fonction pure). */
export const reachabilityFileBaseName = (report: ReachabilityReport, exportedAt: Date): string => {
	const slug =
		(report.title || report.contentId || "contenu")
			.toLowerCase()
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "")
			.slice(0, 60) || "contenu";
	const pad = (value: number) => String(value).padStart(2, "0");
	const stamp = `${exportedAt.getFullYear()}${pad(exportedAt.getMonth() + 1)}${pad(exportedAt.getDate())}-${pad(
		exportedAt.getHours()
	)}${pad(exportedAt.getMinutes())}`;
	return `chemin-acces-${slug}-${stamp}`;
};

/** L'export de donnees : le rapport brut plus les chemins deja lisibles. */
export const buildReachabilityJson = (report: ReachabilityReport, exportedAt: Date): string =>
	JSON.stringify(
		{
			exporteLe: exportedAt.toISOString(),
			contenu: { id: report.contentId, titre: report.title, langue: report.language, cheminPublic: report.publicPath },
			verdict: { code: report.verdict, libelle: report.verdictLabel },
			profondeurMaximale: report.maxDepth,
			boucleDetectee: report.loopDetected,
			resultatTronque: report.truncated,
			chemins: report.paths.map((path) => describePathSteps(report, path)),
			noeuds: report.nodes,
			aretes: report.edges
		},
		null,
		2
	);
